import React from 'react';
import { __ } from 'i18n/index';
import { scoreFor, sidesFor } from 'react/matchHistoryPopup/setSides';
import { replayResult, setSide } from 'react/matchHistoryPopup/historyPopupData';
import { hiddenFromPublicTitle } from 'react/matchHistoryPopup/hiddenFromPublic';

// The league points the player gained or lost, or null when the set moved
// none (friendlies, placement sets).
function pointChangeFor(match, player) {
	const matchPlayer = match.players && match.players[player.id];
	const pointChange = matchPlayer && matchPlayer.match ? matchPlayer.match.point_change : undefined;
	return typeof pointChange === 'number' ? pointChange : null;
}

function TileCharacter({ character }) {
	return character
		? <img src={character.image} alt={character.name} title={character.name} />
		: <span className='history-popup__tile-none'>?</span>;
}

// One half of the tile: the side's first character, or in doubles each
// teammate's first, staggered, in the order the players are named.
function TileSide({ team }) {
	if(team.length > 1) {
		return (
			<span className='history-popup__tile-pair'>
				{team.slice(0, 2).map((side, index) => <TileCharacter key={index} character={side.characters[0]} />)}
			</span>
		);
	}
	return <TileCharacter character={team[0] && team[0].characters[0]} />;
}

// Your side's characters against theirs, split, tinted by game.
function VersusTile({ match, myTeam, theirTeam }) {
	const ladder = match.ladder;
	return (
		<span className='history-popup__tile' data-game={ladder ? ladder.slug : undefined}>
			<TileSide team={myTeam} />
			<TileSide team={theirTeam} />
			{ladder && ladder.mini_logo && <img className='history-popup__tile-game' src={ladder.mini_logo} alt={ladder.name} title={ladder.name} />}
		</span>
	);
}

// A team's characters as one side of the detail line.
const combined = (team) => team.reduce((side, member) => ({
	source: side.source || member.source,
	characters: side.characters.concat(member.characters),
}), { source: null, characters: [] });

const names = (side) => side.characters.map((character) => character.name).join(', ');

// "Melee · Marth vs Sheik"; "mains" marks characters assumed from profiles.
function Characters({ mine, theirs }) {
	if(!mine.characters.length && !theirs.characters.length) {
		return null;
	}
	const assumed = mine.source !== 'played' && theirs.source !== 'played';
	return (
		<React.Fragment>
			{assumed && <span className='history-popup__muted-label'>{__('mains')}</span>}
			<span className={mine.source === 'played' ? 'history-popup__played' : ''}>{names(mine) || '?'}</span>
			{` ${__('vs')} `}
			<span className={theirs.source === 'played' ? 'history-popup__played' : ''}>{names(theirs) || '?'}</span>
		</React.Fragment>
	);
}

function Pips({ games }) {
	const label = __('Games: %s', games.map((won) => (won ? __('won') : __('lost'))).join(', '));
	return (
		<span className='history-popup__pips' role='img' aria-label={label}>
			{games.map((won, index) => <span key={index} className={won ? 'history-popup__pip--won' : 'history-popup__pip--lost'} />)}
		</span>
	);
}

// The set's score from the player's side, colored by who took it, with the
// league points it moved. Friendlies take theirs from linked replays.
function Score({ match, player, sides, replays }) {
	if(match.result_hidden) {
		return <span className='history-popup__score history-popup__score--hidden'>{__('Result hidden')}</span>;
	}
	const pointChange = match.isRanked() ? pointChangeFor(match, player) : null;
	let score = scoreFor(match, sides);
	let outcome = match.hasWinner() ? (sides.won ? 'win' : 'loss') : null;
	if(!score && replays) {
		score = `${replays.wins}-${replays.losses}`;
		outcome = replays.wins === replays.losses ? null : (replays.wins > replays.losses ? 'win' : 'loss');
	}
	if(!score && pointChange === null) {
		return null;
	}
	return (
		<span className={`history-popup__score${outcome ? ` history-popup__score--${outcome}` : ''}`}>
			{score}
			{pointChange !== null &&
			<small className={`history-popup__points history-popup__points--${pointChange >= 0 ? 'gain' : 'loss'}`}
				title={__('Ranked: %s league points', pointChange)}>
				{pointChange >= 0 ? `+${pointChange}` : `−${Math.abs(pointChange)}`}
			</small>
			}
		</span>
	);
}

// Mods get the ladder's flags on a set: "Flagged" while any is open, naming
// them, or "Flags cleared" once mods have closed them all.
function Flags({ flags }) {
	if(!flags || !flags.length) {
		return null;
	}
	const open = flags.filter((flag) => flag.open).map((flag) => flag.label);
	const cleared = flags.filter((flag) => !flag.open).map((flag) => flag.label);
	const title = [open.join(', '), cleared.length ? __('Cleared: %s', cleared.join(', ')) : ''].filter(Boolean).join('. ');
	return open.length
		? <span className='history-popup__chip history-popup__chip--flagged' title={title}>{__('Flagged')}</span>
		: <span className='history-popup__chip history-popup__chip--flags-cleared' title={title}>{__('Flags cleared')}</span>;
}

// How long a finished set took: "14m", "1h 5m" past an hour, "<1m" inside a
// minute.
function SetLength({ match, afterCharacters }) {
	if(!match.start_time || !match.end_time) {
		return null;
	}
	const minutes = Math.max(0, match.end_time.diff(match.start_time, 'minutes'));
	const hours = Math.floor(minutes / 60);
	return (
		<React.Fragment>
			{' '}
			<span className='history-popup__muted-label' title={__('Set length')}>
				{afterCharacters && '· '}{hours ? `${hours}h ${minutes % 60}m` : (minutes ? `${minutes}m` : '<1m')}
			</span>
		</React.Fragment>
	);
}

// A link, or plain text inside a pickable row, which is itself a button.
const profileLink = (person, className, linked = true) => (linked
	? <a key={person.id} className={className} href={person.getProfileUrl ? person.getProfileUrl() : '#'}>{person.username}</a>
	: <span key={person.id} className={className}>{person.username}</span>);

const complaints = (entry) => [
	entry.behavior < 0 ? __('bad behavior') : null,
	entry.connection < 0 ? __('bad connection') : null,
].filter(Boolean).join(', ');

// Mods get the notes left on a set and the feedback its players left that
// says something: a thumbs down or a message.
function ModDetails({ match }) {
	const notes = match.mod_notes || [];
	const feedback = (match.feedback || []).filter((entry) => entry.message || entry.behavior < 0 || entry.connection < 0);
	if(!notes.length && !feedback.length) {
		return null;
	}
	return (
		<ul className='history-popup__mod-details'>
			{notes.map((note) => (
				<li key={`note-${note.id}`}>
					<span className='history-popup__mod-label'>{note.type}</span>
					{note.player && <strong>{note.player.username}</strong>}
					{note.message && <q>{note.message}</q>}
					{note.mod && <span className='history-popup__muted-label'>{__('by %s', note.mod.username)}</span>}
				</li>
			))}
			{feedback.map((entry, index) => (
				<li key={`feedback-${index}`}>
					<span className='history-popup__mod-label'>{__('Feedback')}</span>
					{entry.player && <strong>{entry.player.username}</strong>}
					{complaints(entry) && <span>{complaints(entry)}</span>}
					{entry.message && <q>{entry.message}</q>}
					{entry.hidden && <span className='history-popup__muted-label'>{__('hidden')}</span>}
				</li>
			))}
		</ul>
	);
}

// One set: a versus tile, any doubles partner and the opponents (left out in
// a head-to-head) with the set's type and any dispute, cancellation or (for
// mods) flags, the characters (assumed from mains when none were reported or
// replayed) and the set's length, per-game pips from replays, mod notes and
// feedback for mods, then the score and when. With onSelect the whole row is a
// button to pick the set by, without links or the mod notes inside it.
// showPlayer names the player too ("Anther vs TestUser"), for lists with no
// single point of view.
export default function HistorySetRow({ match, player, headToHead = false, showPlayer = false, onSelect = null, selected = false }) {
	const linked = !onSelect;
	const sides = sidesFor(match, player);
	const opponents = sides.opponents;
	const partners = sides.partners || [];
	const myTeam = [player, ...partners].map((teammate) => setSide(match, teammate));
	const theirTeam = opponents.map((opponent) => setSide(match, opponent));
	const mine = combined(myTeam);
	const theirs = combined(theirTeam);
	// Withheld results come without replay winners; don't count them as losses.
	const replays = match.result_hidden ? null : replayResult(match, player);
	const hiddenTitle = hiddenFromPublicTitle(match.hidden_from_public);
	const content = (
		<React.Fragment>
			<VersusTile match={match} myTeam={myTeam} theirTeam={theirTeam} />
			<span className='history-popup__body'>
				<span className='history-popup__top'>
					{showPlayer && profileLink(player, 'history-popup__opponent', linked)}
					{partners.length > 0 &&
					<React.Fragment>
						<span className='history-popup__muted-label'>{__('with')}</span>
						{partners.map((partner) => profileLink(partner, 'history-popup__partner', linked))}
						{!headToHead && <span className='history-popup__muted-label'>{__('vs')}</span>}
					</React.Fragment>
					}
					{showPlayer && !partners.length && <span className='history-popup__muted-label'>{__('vs')}</span>}
					{!headToHead && opponents.map((opponent) => profileLink(opponent, 'history-popup__opponent', linked))}
					<span className={`history-popup__chip history-popup__chip--${match.isRanked() ? 'ranked' : 'friendlies'}`}>
						{match.isRanked() ? __('Ranked') : __('Friendlies')}
					</span>
					{!!match.is_disputed &&
					<span className='history-popup__disputed' title={match.settled_by ? __('Settled by %s', match.settled_by.username) : undefined}>{__('Disputed')}</span>
					}
					{!!match.is_cancelled && <span className='history-popup__chip history-popup__chip--cancelled'>{__('Cancelled')}</span>}
					<Flags flags={match.flags} />
					{hiddenTitle && <span className='history-popup__chip history-popup__chip--hidden' title={hiddenTitle}>{__('Hidden from public')}</span>}
				</span>
				<span className='history-popup__detail'>
					{match.ladder && <span className='history-popup__muted-label'>{match.ladder.name} ·</span>}
					<Characters mine={mine} theirs={theirs} />
					<SetLength match={match} afterCharacters={mine.characters.length > 0 || theirs.characters.length > 0} />
				</span>
				{replays && <Pips games={replays.games} />}
				{linked && <ModDetails match={match} />}
			</span>
			<span className='history-popup__side'>
				<Score match={match} player={player} sides={sides} replays={replays} />
				{linked
					? <a className='history-popup__when' href={match.getUrl()}>{match.startTimeCalendar()}</a>
					: <span className='history-popup__when'>{match.startTimeCalendar()}</span>}
			</span>
		</React.Fragment>
	);
	return onSelect
		? <button type='button' className='history-popup__set' aria-pressed={selected} onClick={() => onSelect(match)}>{content}</button>
		: <li className='history-popup__set'>{content}</li>;
}
